import React, { useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Send, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill all required fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="container-gc px-4 sm:px-6 py-10 sm:py-16">
      <div className="max-w-2xl mx-auto mb-10 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">{t.contact.title}</h1>
        <p className="mt-3 text-stone-700 text-lg leading-relaxed">{t.contact.subtitle}</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {success ? (
          <div data-testid="contact-success" className="card-gc p-8 text-center">
            <CheckCircle2 size={48} className="text-green-600 mx-auto mb-3" />
            <p className="text-lg font-semibold text-stone-900">{t.contact.success}</p>
            <button
              onClick={() => setSuccess(false)}
              className="btn-ghost mt-5"
              data-testid="contact-send-another"
            >
              {t.contact.send_another}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-gc p-6 sm:p-8 space-y-5">
            {error && (
              <div data-testid="contact-error" className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
                {error}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.contact.form_name} *</label>
                <input
                  data-testid="contact-name-input"
                  className="input-gc"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.contact.form_email} *</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className="input-gc"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.contact.form_phone}</label>
              <input
                data-testid="contact-phone-input"
                className="input-gc"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.contact.form_message} *</label>
              <textarea
                data-testid="contact-message-input"
                className="input-gc py-3 !h-auto min-h-[160px]"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
              />
            </div>
            <button data-testid="contact-send-btn" type="submit" disabled={busy} className="btn-primary w-full">
              <Send size={18} />
              {busy ? t.common.loading : t.contact.send}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Contact;
